/**
 * Centralized help content for every info icon in the app.
 * Each entry powers a click-to-open modal that explains a metric,
 * chart, or interactive control to a first-time user.
 */

export interface HelpEntry {
  title: string;
  description: string;
  interactiveNote?: string;
  source?: string;
}

export const HELP: Record<string, HelpEntry> = {

  // ─── GLOBAL / SHARED ASSUMPTIONS ───

  sharedAssumptions: {
    title: 'Shared Assumptions',
    description:
      'These parameters feed every calculation in the configurator. They represent AlterMe\'s current operating model — monthly lead volume, funnel conversion rates, cost structure, and the feature-lift dampener. Changing any value here instantly updates all package cards, every chart, and every financial projection across all tabs. Parameters in the "Package Defaults" section can be overridden per-package.',
    interactiveNote:
      'Click "Expand" to reveal sliders for each assumption. Drag a slider to see how P&L and demand shift in real time.',
  },

  s1Leads: {
    title: 'Monthly S1 Leads',
    description:
      'The number of Stage 1 kit purchases per month. This is an AlterMe operating input, not a conjoint output. The configurator applies the conjoint choice rate to this volume as a scenario proxy (S2 Units = S1 Leads × Adjusted Choice Rate). The conjoint itself was measured on cold survey respondents, not S1 buyers.',
    interactiveNote:
      'Increasing S1 leads scales scenario S2 units and revenue proportionally. The ProForma range is 5,300–8,501 per month.',
    source: 'ProForma FEB 2026',
  },

  kitReturnRate: {
    title: 'Kit Return Rate',
    description:
      'The percentage of S1 purchasers who actually return the DNA swab. Currently assumed at 85%. This is an AlterMe operational parameter, not a conjoint output. The conjoint rate was measured on cold survey respondents who never entered the kit/consultation funnel. Kit Return Rate is used when computing the Required Close Rate to bridge conjoint demand to the consultation funnel.',
    interactiveNote:
      'Lowering this rate increases the Required Close Rate needed for the conjoint demand scenario to materialize.',
    source: 'AlterMe Assumption Calculator',
  },

  consultRate: {
    title: 'Consultation Rate',
    description:
      'Of the customers who returned their kit, what percentage book and attend a consultation? Currently assumed at 85%. This is an AlterMe operational parameter. Together with Kit Return Rate, it defines the funnel path from S1 to consultation. The Required Close Rate uses both rates to bridge conjoint demand to the consultation stage.',
    interactiveNote:
      'A lower consultation rate means the Required Close Rate would need to be higher for the conjoint scenario to materialize.',
    source: 'AlterMe Assumption Calculator',
  },

  totalDeductionRate: {
    title: 'Total Deduction Rate',
    description:
      'The all-in percentage deducted from gross sale price before costs are subtracted. Includes payment processing fees, refunds (14%), cancellations (3%), chargebacks, and other deductions. At $1,499, a 26.6% deduction rate yields $1,100 net — matching the CFO\'s FEB 2026 model. This is broader than just refunds.',
    interactiveNote:
      'Increasing the deduction rate reduces net revenue per unit and directly lowers net income per sale.',
    source: 'CFO FEB 2026: $1,499 sale nets $1,100 after all deductions = 26.6%',
  },

  fixedCosts: {
    title: 'Monthly Fixed Costs',
    description:
      'Total monthly overhead that does not vary with the number of S2 units sold — includes salaries, rent, marketing spend, platform costs, etc. This is subtracted from net revenue before arriving at P&L. Higher fixed costs raise the breakeven point.',
    interactiveNote:
      'Increasing fixed costs pushes all P&L values down and increases the number of S2 units needed to break even.',
    source: 'ProForma FEB 2026 — ~$420K–$560K range',
  },

  s1CostPerUnit: {
    title: 'S1 Cost per Kit',
    description:
      'The cost AlterMe incurs for each S1 kit sold. This is the customer acquisition cost at the S1 stage. Combined with the S2 close rate, it determines the S1 acquisition cost per S2 sale: S1 Cost / Close Rate. At $93.39 per kit and 20% close rate, it takes ~5 S1s ($467) to produce one S2 sale.',
    interactiveNote:
      'Higher S1 costs directly increase the acquisition cost per S2 sale, reducing net income.',
    source: 'CFO FEB 2026 Assumption Calculator: $93.39 per S1 kit',
  },

  s2CloseRate: {
    title: 'S2 Close Rate',
    description:
      'The percentage of S1 customers who ultimately purchase an S2 package after going through the kit return and consultation process. This is the actual operational close rate, distinct from the conjoint-derived "Required Close Rate." At 20%, it takes 5 S1s to produce one S2 sale. Improving close rate is one of the most powerful levers — going from 20% to 30% reduces acquisition cost by $156 per sale.',
    interactiveNote:
      'Higher close rate = fewer S1s needed per sale = lower acquisition cost = higher net income.',
    source: 'CFO FEB 2026: ~20% operational close rate ("almost 5 S1s per sale")',
  },

  s2RepCost: {
    title: 'S2 Rep Cost per Sale',
    description:
      'The per-sale cost of the S2 sales representative who conducts the consultation and closes the sale. This includes allocated base salary and any per-sale commission. This is a direct cost subtracted from each S2 sale.',
    interactiveNote:
      'Higher rep costs reduce net income per sale. This cost is fixed per sale regardless of package price.',
    source: 'CFO FEB 2026: $189 per S2 sale',
  },

  featureCosts: {
    title: 'Per-Feature Variable Costs',
    description:
      'Each feature has its own COGS — the incremental cost of including that feature in a package. The Smart Biometrics Ring ($136) includes hardware, warehousing, shipping, and logistics. The DNA Health Report ($75) covers lab processing. A package\'s total cost per sale = S1 acquisition + S2 rep + sum of enabled feature COGS. Fewer features = lower COGS = higher net income per sale.',
    interactiveNote:
      'Adjust individual feature costs to see how they affect margin and P&L for packages that include them. The total across all features is shown at the bottom.',
    source: 'CFO FEB 2026: Ring $136 (fully loaded), DNA $75, Workouts $10, Body Comp $12, Recovery $5, Wellness Plan $2, Nutrition $2 = $242 total.',
  },

  featureScaling: {
    title: 'Feature Lift Scaling',
    description:
      'A multiplicative dampener (0 to 1) applied to the total feature lift before adjusting the base choice rate. The conjoint study measures each feature\'s lift in isolation. In reality, bundling many features together yields diminishing returns — the sum of individual lifts overstates the true bundle value. A scaling of 0.5 means "use half of the raw lift sum."',
    interactiveNote:
      'At 0, features have no effect on choice rate (base rate only). At 1, the full raw lift sum is applied. The default 0.5 is a conservative middle ground.',
    source: 'CBC Study methodology — dampened bundle estimation',
  },

  // ─── PACKAGE BUILDER ───

  priceSelect: {
    title: 'Package Price',
    description:
      'The S2 price point for this package. The conjoint study tested five specific prices: $499, $999, $1,499, $1,699, and $1,899. Each price has a measured concept-level choice rate — the probability that a respondent chooses a concept shown at that price. Higher prices reduce demand but increase revenue per unit.',
    interactiveNote:
      'Selecting a different price instantly recalculates the base choice rate, adjusted choice rate, S2 units, revenue, P&L, and breakeven for this package.',
    source: 'CBC Study, n=1,001 — 5 price levels tested',
  },

  paymentTerms: {
    title: 'Payment Terms',
    description:
      'How customers pay for the package. The study tested three options: Pay in Full, Monthly (0% APR), and Monthly (with interest). In the study, all three had nearly equal share (~33% each), suggesting payment structure is not a major purchase driver. This selector is included for scenario modeling but does not currently alter the choice rate.',
    interactiveNote:
      'Changing payment terms does not affect the financial calculations in this configurator — choice rates are price-driven, not payment-driven.',
    source: 'CBC Study — payment attribute shares',
  },

  featureToggles: {
    title: 'Feature Toggles',
    description:
      'Turn individual features ON or OFF for this package. Each feature has a measured "lift" — the increase in conditional share when included vs. excluded. Features are ranked: Smart Biometrics Ring (+13.2%), Workout Sessions (+11.8%), Body Composition Analysis (+9.6%), Recovery Sessions (+8.7%), DNA Health Report (+8.6%), Personalized Wellness Plan (+8.4%), Nutrition Tracking (+7.5%).',
    interactiveNote:
      'Toggling a feature ON adds its lift to the total feature lift, which increases the adjusted choice rate and therefore S2 units and revenue. But more units also mean more variable costs, so the P&L impact depends on margin.',
    source: 'CBC Study — conditional share analysis',
  },

  adjChoiceRate: {
    title: 'Adjusted Choice Rate',
    description:
      'The conjoint-derived demand rate for this package configuration. Calculated as: Base Choice Rate × (1 + Total Feature Lift × Feature Scaling). The base rate comes from cold-market survey data; the lift comes from enabled features. This is a scenario input — it represents cold-market attractiveness, not a direct prediction of S1-to-S2 conversion.',
    source: 'Calculated: base rate × (1 + lift × scaling)',
  },

  s2Units: {
    title: 'S2 Units per Month (Scenario)',
    description:
      'The scenario-projected number of S2 purchases per month if conjoint demand translated directly to purchases. Calculated as S1 Leads × Adjusted Choice Rate. This is NOT a sales forecast — the actual S1-to-S2 path goes through kit return (85%), consultation (85%), and a close decision (20% default, 30% proven), none of which the conjoint models. See Required Close Rate for the bridge to your operational funnel.',
    source: 'Calculated: S1 Leads × Adjusted Choice Rate',
  },

  netRevenue: {
    title: 'Net Revenue',
    description:
      'Gross revenue minus all deductions (fees, refunds, cancellations, chargebacks). Calculated as S2 Units × Price × (1 − Total Deduction Rate). At the default 26.6% deduction rate (CFO FEB 2026), you keep 73.4 cents of every dollar — a $1,499 sale nets $1,100. This is the top-line number that variable and fixed costs are subtracted from.',
    source: 'Calculated: S2 × Price × (1 − 26.6% deduction rate)',
  },

  monthlyPnL: {
    title: 'Monthly P&L (Profit & Loss)',
    description:
      'Net Revenue minus Fixed Costs minus Variable Costs. In single-product mode, full fixed costs are applied to one package. In portfolio mode, fixed costs are applied once across all packages — see Portfolio P&L for the combined view. Green (+) indicates profitability. Red (−) indicates a loss.',
    source: 'Calculated: Net Revenue − Fixed Costs − Variable Costs',
  },

  cannibalizationRate: {
    title: 'Cannibalization Rate',
    description:
      'The estimated percentage of higher-priced package demand that trades down to cheaper packages when multiple tiers are offered simultaneously. At 15% (default), 15% of customers who would have bought the $1,499 package instead buy the $999 package because it exists. Applied in split demand mode only. Set to 0% for no cannibalization (optimistic) or up to 50% for aggressive trade-down assumptions.',
    interactiveNote:
      'Drag the slider in the Portfolio P&L Summary to see how cannibalization affects combined P&L. Higher rates shift units from expensive to cheap packages, typically reducing total revenue.',
    source: 'User-adjustable assumption (default 15%)',
  },

  breakevenUnits: {
    title: 'Breakeven Units',
    description:
      'The number of S2 units needed per month to make P&L exactly zero. Calculated as Fixed Costs ÷ (Price × (1 − Deduction Rate) − Variable Cost per Unit). If the margin per unit is negative (variable cost exceeds net revenue per unit), breakeven is impossible — shown as "N/A."',
    source: 'Calculated: Fixed Costs ÷ Margin per Unit',
  },

  impliedCloseRate: {
    title: 'Required Close Rate',
    description:
      'What would AlterMe\'s in-consultation close rate need to be for this conjoint demand scenario to materialize through the actual funnel? Calculated as Adjusted Choice Rate ÷ (Kit Return Rate × Consultation Rate). Green (≤28%) = conservative, well below the proven 30%. Amber (28-35%) = stretch, near the limit of proven performance. Red (>35%) = aggressive, would require significantly better close performance than demonstrated.',
    source: 'Calculated: Adj. Rate ÷ (Kit Return × Consult). Compare to proven 30%.',
  },

  comparisonTable: {
    title: 'Head-to-Head Comparison',
    description:
      'A side-by-side view of all configured packages across key metrics, with a Portfolio Total column. The "Best" column highlights which package wins on each metric — useful for quickly identifying trade-offs. The trophy icon marks the leader for each row.',
  },

  // ─── PRICE EXPLORER ───

  selectedPrice: {
    title: 'Selected Price',
    description:
      'Choose which of the five tested price points to analyze in detail. This selection drives the highlighted row in the elasticity table, the vertical reference line on charts, and the revenue waterfall calculations.',
    interactiveNote:
      'Changing the selected price updates the elasticity table highlighting, the revenue waterfall, and the feature analysis calculations on other tabs.',
    source: 'CBC Study — 5 tested price levels',
  },

  elasticityCurve: {
    title: 'Price Elasticity Curve',
    description:
      'Shows two relationships on one chart: (1) how choice rate decreases as price rises (teal line, left axis), and (2) how revenue per prospect changes (amber dashed line, right axis). Revenue per Prospect = Price × Choice Rate — it initially rises with price before falling off. The peak of the amber line is the revenue-maximizing price point. The teal dashed vertical line marks your currently selected price.',
    source: 'CBC Study — concept-level choice rates at 5 prices',
  },

  demandCurve: {
    title: 'Demand Curve (Interpolated)',
    description:
      'A smooth demand curve created by linearly interpolating between the five tested price points. The shaded area underneath emphasizes the demand region. Solid dots mark the actual study data points — the curve between them is an estimate. This visualization helps identify the shape of price sensitivity across the full price range.',
    source: 'CBC Study — interpolated between 5 tested prices',
  },

  elasticityTable: {
    title: 'Elasticity Data Table',
    description:
      'The raw numbers behind the price curves. For each tested price: how many concepts were shown, how many were chosen, the resulting choice rate, revenue per prospect (price × rate), cumulative revenue, and price elasticity (% change in demand ÷ % change in price). The highlighted row matches your selected price.',
    source: 'CBC Study — raw concept-level data',
  },

  revenuePerShowing: {
    title: 'Revenue per Prospect',
    description:
      'The expected revenue from showing a single product concept to one respondent at a given price. Calculated as Price × Choice Rate. This metric captures the trade-off between price and demand — a higher price brings more revenue per sale but fewer sales. The peak of this curve identifies the revenue-maximizing price.',
    source: 'Calculated: Price × Concept-level Choice Rate',
  },

  revenueWaterfall: {
    title: 'Revenue Waterfall',
    description:
      'A step-by-step breakdown of how gross revenue turns into P&L. Starting with gross revenue (all features ON at the selected price), then subtracting refunds, fixed costs, and variable costs to arrive at net P&L. Green bars are positive flows, red bars are deductions. The final bar shows whether you end up profitable or at a loss.',
    source: 'Calculated from current assumptions, all features ON',
  },

  elasticity: {
    title: 'Price Elasticity',
    description:
      'A measure of demand sensitivity to price changes. Calculated as (% change in choice rate) ÷ (% change in price) between adjacent price points. A value of −1.0 means a 10% price increase causes a 10% demand drop. More negative = more price-sensitive. Values closer to 0 mean demand is relatively inelastic (less responsive to price changes).',
    source: 'Calculated between adjacent tested price points',
  },

  // ─── FEATURE ANALYSIS ───

  featureLiftRanking: {
    title: 'Feature Lift Ranking',
    description:
      'Features ranked by their "lift" — the difference in conditional share when a feature is included vs. excluded in a concept. A higher lift means that feature makes the product more attractive. Smart Biometrics Ring leads at +13.2%, while Nutrition Tracking is last at +7.5%. Note: lifts are measured in isolation; the actual impact when bundling multiple features is dampened by the Feature Scaling parameter.',
    source: 'CBC Study — conditional share when included vs. excluded',
  },

  featureImpactMatrix: {
    title: 'Feature Impact Matrix',
    description:
      'Shows the incremental impact of adding each feature individually (starting from zero features) at the currently selected price. Three columns: Choice Rate Delta (how much the rate increases), Revenue Delta (additional net revenue), and P&L Delta (net profit impact after costs). Cell shading intensity indicates magnitude — darker green = larger positive impact.',
    source: 'Calculated: each feature added in isolation at selected price',
  },

  bundleOptimizer: {
    title: 'Feature Bundle Optimizer',
    description:
      'An exhaustive analysis of all 128 possible ON/OFF combinations for the 7 features. Each bundle is scored by P&L at the current price and assumptions, then ranked best-to-worst. The top 10 and bottom 5 are shown. Your current package bundle is highlighted with a checkmark. Use this to find the profit-maximizing feature set — it may not be "all ON."',
    source: 'Calculated: 2^7 = 128 combinations, ranked by P&L',
  },

  marginalValue: {
    title: 'Marginal Feature Value',
    description:
      'For each feature in the first package\'s current bundle, this shows the P&L impact of toggling it. For enabled features: "what would we lose by removing it?" For disabled features: "what would we gain by adding it?" Bars point right (positive) if the feature adds value, left (negative) if removing it would improve P&L. Brighter bars = currently enabled; faded = not enabled.',
    source: 'Calculated: P&L delta from toggling each feature',
  },

  featureLift: {
    title: 'Feature Lift',
    description:
      'The difference in conditional share (among product choosers) when a feature is included in the concept vs. excluded. For example, Smart Ring has a lift of +13.2%: products with it are chosen 56.6% of the time vs. 43.4% without it. Higher lift = more valuable feature.',
    source: 'CBC Study — conditional share analysis',
  },

  choiceRateDelta: {
    title: 'Choice Rate Delta',
    description:
      'The change in adjusted choice rate when adding this single feature to a zero-feature bundle at the selected price. This captures the feature\'s standalone contribution to demand, accounting for the feature scaling dampener.',
    source: 'Calculated: rate with feature − rate without',
  },

  // ─── SEGMENT DEEP DIVE ───

  segmentOverview: {
    title: 'Segment Overview',
    description:
      'Three distinct customer segments identified via k-means clustering on part-worth utilities. Price-Sensitive Buyers (52.6%) are the mainstream market with moderate price sensitivity. Low-Interest Prospects (28.4%) show near-zero demand at any price — effectively unreachable. Budget Seekers (19.0%) want the product but only at ~$499. Each card shows segment size, average none-utility, and a mini demand sparkline.',
    source: 'CBC Study — k-means clustering, n=1,001',
  },

  avgNoneUtility: {
    title: 'Average None Utility',
    description:
      'The average utility score for the "none" option in this segment. A higher none-utility means respondents in this segment are more likely to choose "none of these" — i.e., not buy any product. Low-Interest Prospects has the lowest (0.035), meaning they reject products for reasons other than the "none" option being attractive. Budget Seekers at 0.493 means "none" is moderately tempting compared to the product.',
    source: 'CBC Study — part-worth utilities by segment',
  },

  segmentPriceCurves: {
    title: 'Segment Price Curves',
    description:
      'Overlays the demand curve for each segment plus the overall weighted average. This reveals the dramatically different price sensitivity across segments: Budget Seekers have a steep cliff between $499 and $999; Price-Sensitive Buyers show a gentler slope; Low-Interest Prospects is a flat line near zero. The overall curve (dashed blue) is the weighted average of all three.',
    source: 'CBC Study — concept-level rates by segment',
  },

  segmentDemandTable: {
    title: 'Segment Demand Table',
    description:
      'At the selected price, shows each segment\'s contribution to overall demand. Weight = segment\'s share of the population. Choice Rate = demand within that segment. Weighted Demand = Weight × Choice Rate. The total of all weighted demands should approximately equal the overall concept-level choice rate from the study.',
    source: 'CBC Study — weighted segment composition',
  },

  weightedDemand: {
    title: 'Weighted Demand',
    description:
      'A segment\'s contribution to the overall choice rate, calculated as Segment Weight × Segment Choice Rate. For example, if Price-Sensitive Buyers (52.6%) have a 31.2% choice rate at $1,499, their weighted demand is 0.526 × 0.312 = 16.4%. Sum all segments\' weighted demands to get the overall rate.',
    source: 'Calculated: segment weight × segment choice rate',
  },

  bimodalStructure: {
    title: 'Bimodal Market Structure',
    description:
      'Stacked bar chart decomposing overall demand into segment contributions at each price point. At $499, Budget Seekers (amber) contribute significant demand. Above $999, demand is driven almost entirely by Price-Sensitive Buyers (teal). Low-Interest Prospects (red) contribute near-zero demand at all prices. The chart illustrates that the overall demand curve is a blend of structurally different segment behaviors.',
    source: 'CBC Study — segment-weighted demand decomposition',
  },

  noneAnalysis: {
    title: 'None Analysis',
    description:
      'Breaks down how respondents used the "none of these" option across all choice tasks. The overall none rate of 38.5% means that in roughly 4 out of every 10 tasks, respondents declined all products. This panel decomposes that into three groups to understand the true addressable market.',
    source: 'CBC Study — none-option analysis, n=1,001',
  },

  alwaysNone: {
    title: 'Always-None Respondents',
    description:
      'Respondents who chose "none" in every single one of their 15 choice tasks — they never picked any product at any price or feature combination. These 217 people (21.7%) are truly unreachable and should be excluded from addressable market sizing. No amount of pricing or feature changes will convert them.',
    source: 'CBC Study — 217 of 1,001 respondents',
  },

  neverNone: {
    title: 'Never-None Respondents',
    description:
      'Respondents who always chose a product in every choice task — they never selected "none." These 427 people (42.7%) are highly engaged and always in-market. They represent the most convertible segment of the study population.',
    source: 'CBC Study — 427 of 1,001 respondents',
  },

  addressableMarket: {
    title: 'Addressable Market',
    description:
      'The 784 respondents (78.3%) who chose a product at least once across their 15 tasks. This excludes the 21.7% always-none group. When projecting real-world demand, this is the realistic ceiling — not 100% of S1 leads, but roughly 78% are potentially convertible with the right offer.',
    source: 'CBC Study — 784 of 1,001 respondents',
  },

  // ─── FINANCIAL MODEL ───

  assumptionGap: {
    title: 'Assumption Gap Analysis',
    description:
      'A side-by-side comparison of AlterMe\'s ProForma assumptions alongside conjoint-derived observations. These come from different analytical frameworks (operational sales data vs. cold-market survey data) and are presented for context, not direct comparison. Red "HIGH" risk items indicate where the two frameworks diverge most significantly.',
    source: 'AlterMe ProForma FEB 2026 vs. CBC Study',
  },

  riskLevel: {
    title: 'Risk Level',
    description:
      'A qualitative assessment of the gap between the financial model assumption and the conjoint evidence. HIGH (red) = the assumption is dangerously optimistic and could lead to major revenue shortfalls. MEDIUM (amber) = a notable gap that warrants attention. LOW (green) = the assumption is roughly in line with the data or the gap is favorable.',
  },

  sensitivityHeatmap: {
    title: 'Sensitivity Heatmap',
    description:
      'A two-dimensional analysis showing Monthly P&L for every combination of Price (rows) and S1 Lead Volume (columns). Green cells = profit, red cells = loss, with intensity proportional to magnitude. The highlighted column marks your current S1 lead assumption. Hover any cell for exact numbers. Use this to find the combinations where profitability is achievable and understand how sensitive the result is to lead volume.',
    source: 'Calculated: Price × S1 Leads matrix with base choice rates',
  },

  breakevenAnalysis: {
    title: 'Breakeven Analysis',
    description:
      'For each price point, shows how many S1 leads per month would be needed to reach P&L = $0. The amber dashed line marks the maximum available S1 volume (8,501). Bars that stay left of this line (green) are achievable; bars that extend beyond (red) would require more leads than are available — meaning that price point cannot break even under current conditions.',
    source: 'Calculated: Fixed Costs ÷ Margin per Unit ÷ Choice Rate',
  },

  // ─── EXECUTIVE SUMMARY ───

  executiveSummary: {
    title: 'Executive Summary',
    description:
      'A board-ready overview of the conjoint study\'s key findings. Shows the profit-maximizing price, top feature, addressable market size, and risk assessment. All numbers are derived from the CBC study data and your current assumptions.',
    source: 'CBC Study, n=1,001 + current configurator assumptions',
  },

  recommendedConfig: {
    title: 'Top Scenario by P&L',
    description:
      'The price point and feature bundle that produces the highest monthly P&L under your current assumptions. Computed by testing all 128 feature combinations across all 5 price points (640 scenarios). This is a scenario projection, not a sales recommendation — operational factors not captured by the conjoint may affect actual performance.',
    source: 'Calculated: highest P&L across 640 price × feature scenarios',
  },

  // ─── WILLINGNESS TO PAY ───

  wtpTab: {
    title: 'WTP Analysis',
    description:
      'Willingness-to-Pay analysis translates conjoint choice rates into dollar values. Instead of asking "what % would buy at $X?" this tab answers "how many dollars is each feature worth?" and "what\'s the maximum price each segment will tolerate?" All calculations use the demand-equivalence method: finding the price drop that matches a feature\'s demand lift.',
    source: 'Derived from CBC Study demand curves, n=1,001',
    interactiveNote: 'Use the Reference Price selector to see how WTP values change at different price points.',
  },

  wtpByFeature: {
    title: 'Willingness to Pay by Feature',
    description:
      'The implied dollar value each feature adds, calculated using the demand-equivalence method. For each feature: "Adding this feature increases purchase probability by X%. To achieve the same demand without it, you\'d need to drop the price by $Y." That $Y is the feature\'s WTP value.',
    source: 'Calculated from conjoint choice rates via demand curve interpolation',
  },

  wtpBySegment: {
    title: 'Maximum Price by Segment',
    description:
      'The highest price each market segment will tolerate while maintaining at least 10% purchase probability. Budget Seekers have a sharp cliff above $499. Price-Sensitive Buyers will pay up to ~$1,700. Low-Interest Prospects won\'t buy at any price.',
    source: 'CBC Study segment price curves, 10% demand threshold',
  },

  priceValueMap: {
    title: 'Price-Value Map',
    description:
      'Plots each package on a price (X) vs. feature value score (Y) grid. Upper-left quadrant indicates high value relative to price; lower-right indicates low value relative to price. Baseline price-only dots (gray) provide a reference for where packages without feature differentiation would sit.',
    interactiveNote: 'Change package features or prices to see the dots move.',
  },

  wtpSummary: {
    title: 'WTP Summary Table',
    description:
      'All 7 features ranked by their implied dollar value at the selected price using the demand-equivalence method. Higher-ranked features contribute more perceived value at the current price point.',
    source: 'Calculated from conjoint demand-equivalence method',
  },

  // ─── RISK SUMMARY ───

  riskSummary: {
    title: 'Key Risks: Model vs. Conjoint',
    description:
      'Highlights every assumption flagged HIGH risk — where the financial model diverges most from conjoint evidence. Each row shows the ProForma assumption alongside the conjoint observation. These come from different analytical frameworks; the flags indicate where the divergence is largest and most worth investigating.',
    source: 'ASSUMPTION_GAPS table (HIGH risk items only)',
  },

  // ─── MARKET OVERVIEW ───

  marketAtAGlance: {
    title: 'Market at a Glance',
    description:
      'Three distinct customer segments identified through k-means clustering on conjoint part-worth utilities. Price-Sensitive Buyers (52.6%) are the mainstream opportunity. Budget Seekers (19%) want the product but only below ~$500. Low-Interest Prospects (28.4%) show near-zero demand at any configuration. This segmentation is the foundation for evaluating whether multiple product tiers are warranted.',
    source: 'CBC Study — k-means clustering, n=1,001',
  },

  demandDriver: {
    title: '#1 Demand Driver',
    description:
      'The feature with the highest cold-market demand lift when included in a product concept. Measured as the difference in conditional share when included vs. excluded, across all respondents.',
    source: 'CBC Study — conditional share analysis',
  },

  revenueMaximizingPrice: {
    title: 'Revenue-Maximizing Price',
    description:
      'The price point that maximizes revenue per prospect (Price × Concept-Level Choice Rate). This is the peak of the revenue-per-prospect curve. It balances higher price against lower demand volume. This is a conjoint-derived metric, not a P&L projection — it does not account for costs.',
    source: 'CBC Study — calculated from concept-level choice rates',
  },

  demandSpread: {
    title: 'Demand Spread',
    description:
      'The ratio between the highest and lowest concept-level choice rates across the five tested price points. A larger spread indicates greater price sensitivity. This 3× gap ($499 at 36.2% vs. $1,899 at 11.9%) confirms there are structurally different buyer populations in the market.',
    source: 'CBC Study — ratio of choice rates at price extremes',
  },

  // ─── PACKAGE BUILDER (NEW) ───

  combinedMarketReach: {
    title: 'Combined Market Reach',
    description:
      'The theoretical maximum market capture if all configured packages are offered simultaneously. For each segment, takes the highest adjusted choice rate across all packages, then weights by segment size. This is a ceiling estimate — actual combined demand would be lower because some customers who would buy a premium tier would trade down if a cheaper option is available. Use the split demand mode and cannibalization slider for a more conservative estimate.',
    source: 'Calculated: sum of max(segment rates) across packages, weighted by segment size',
  },

  segmentDemandScenario: {
    title: 'Segment Demand (Scenario)',
    description:
      'Per-segment adjusted choice rates for each package. These apply overall feature lifts uniformly to each segment\'s base rate at the package price. Segment-specific feature interactions were not measured in the study, so these are approximations.',
    source: 'Calculated: segment base rate × (1 + total lift × scaling)',
  },

  scenarioEconomics: {
    title: 'Scenario Economics',
    description:
      'Financial projections derived from conjoint demand scenarios. These show what economics would look like if conjoint cold-market demand translated directly to S2 purchases. Your actual funnel (kit return → consultation → close) operates differently. Use these to compare relative attractiveness across configurations, not as sales forecasts.',
    source: 'Calculated from conjoint demand × operating assumptions',
  },

  // ─── SEGMENTS (NEW) ───

  segmentPackageMatrix: {
    title: 'Per-Package Segment Demand',
    description:
      'A matrix showing how each configured package performs within each market segment. Each cell is the scenario-projected choice rate: the segment\'s base rate at the package price, adjusted for the package\'s feature bundle. The "Overall" row is the weighted average. This answers: "If I offered these three tiers, which segments would each attract?"',
    source: 'Calculated: segment base rate × (1 + total lift × scaling). Feature lifts applied uniformly.',
  },

  // ─── FEATURE VALUE (NEW) ───

  referencePrice: {
    title: 'Reference Price',
    description:
      'The price point used as a baseline for WTP and feature value calculations. WTP values show how much each feature is worth in dollar terms at this reference price. Different reference prices produce different WTP values because feature demand lifts interact with the price-demand curve shape.',
    interactiveNote: 'Changing the reference price updates all WTP calculations on this tab.',
    source: 'User selection from 5 tested price points',
  },

  // ─── SUGGEST TIERS ───

  suggestTiers: {
    title: 'Suggest Tiers',
    description:
      'Tool to test a differentiated three-tier lineup from conjoint data. Set Premium, Core, and Starter prices (and how many top-lift features go in Starter: 1, 2, or 3). Click Suggest Tiers to see a preview: Premium uses the highest-P&L feature bundle at that price; Core uses the highest-P&L bundle with at least 2 fewer features than Premium; Starter uses the top N demand-driving features by lift. Apply to update the Package Builder cards, or Cancel to dismiss. "From defaults then suggest" resets packages to the app default, then applies the suggestion with your current price and Starter settings — useful to see the suggested change clearly.',
    interactiveNote: 'Use the price dropdowns and Starter-features choice to explore scenarios. Preview shows what will be applied before you commit. After applying, a short summary shows what changed (e.g. feature counts per package). You can adjust any package freely after.',
    source: 'Calculated from rankAllFeatureBundles at each price point',
  },

  featureAllocation: {
    title: 'Suggested Feature Allocation',
    description:
      'Features ranked by implied WTP (willingness to pay) at the selected reference price, then allocated across three suggested tiers. The top 3 features by WTP go to Premium (they justify the higher price), the middle 2 go to Core, and the bottom 2 go to Starter. This is a starting suggestion based on independent feature values.',
    source: 'Calculated from WTP demand-equivalence method. Feature interactions were not measured.',
  },

  // ─── VOICE OF MARKET ───

  voiceOfMarket: {
    title: 'Voice of Market',
    description:
      'Open-ended survey responses from 1,000 conjoint respondents, linked to their actual choice behavior and demographics. Each respondent answered questions about health goals, willingness-to-pay factors, and brand perception. Their responses are paired with behavioral data from the conjoint exercise (products chosen, prices, features) plus survey data on demographics, feature importance ratings, feature rankings, spending habits, and health goals.',
    source: 'CBC Conjoint Study, n=1,000 respondents with matched verbatim + behavioral + demographic data',
  },

  saidVsDid: {
    title: 'Said vs. Did — Feature Preferences',
    description:
      'Compares what respondents stated matters to them (feature importance ratings from Q5 and feature rankings from Q6) against what they actually chose in the conjoint exercise (revealed preference from task choices). A positive gap means the feature performed better in actual choices than stated importance would suggest. A negative gap means respondents said it was important but did not prioritize it when making trade-off decisions. Gaps exceeding 15 percentage points are highlighted.',
    source: 'Q5 (5-point importance scale) and Q6 (1-7 ranking) vs. conjoint task choice behavior',
  },

  groupProfile: {
    title: 'Group Profile',
    description:
      'An aggregate summary of the currently filtered respondent group. Shows demographic composition (gender, average age, income distribution), conjoint behavior (average none rate, average chosen price, price objection rate), and stated priorities (top health goal, top-ranked feature, wellness spending patterns). All statistics are computed from the filtered set and update as filters change.',
    source: 'Computed from filtered respondent data — demographics from survey, behavior from conjoint tasks',
  },

  marketingStrategy: {
    title: 'Marketing Strategy',
    description:
      'Data-driven marketing strategy generated from conjoint data, verbatim responses, and demographic profiles. Select a package to see its market sizing (addressable units by segment), audience persona (derived from respondents who align with the package\'s price and features), and AI-generated messaging framework (Value Proposition, Message House, StoryBrand BrandScript). All statistics trace back to the CBC study or respondent demographics.',
    source: 'Computed from CBC Conjoint Study + verbatim data + respondent demographics',
  },

  verbatimWall: {
    title: 'Respondent Verbatims',
    description:
      'Individual respondent cards showing their open-ended survey responses alongside their conjoint behavior profile. Each card includes health goals, willingness-to-pay factors, price reactions (if any), brand perception, and a behavioral footer with products chosen, average price, price distribution, and top feature preferences. Cards can be expanded to show demographic details. Text is shown exactly as respondents wrote it.',
    source: 'CBC Conjoint Study — open-ended questions Q1, Q7, Q8, Q12 + conjoint task behavior',
  },

  // ─── PORTFOLIO P&L ───

  contributionMargin: {
    title: 'Contribution Margin',
    description:
      'Net revenue minus all variable costs (S1 acquisition, S2 rep cost, feature COGS) for this package. Fixed costs are NOT included — they are applied once at the portfolio level. This shows what each package contributes to covering the company\'s fixed overhead. A positive contribution margin means the package is pulling its weight; a negative one means every sale loses money before fixed costs are even considered.',
    source: 'Calculated: (S2 Units × Price × (1 − Deduction Rate)) − (S2 Units × (S1 Acq + S2 Rep + Feature COGS))',
  },

  portfolioPnL: {
    title: 'Portfolio P&L',
    description:
      'The combined monthly profit/loss across all packages after subtracting fixed costs once. Formula: Sum of all package contribution margins − monthly fixed costs. This is the correct way to evaluate a multi-package strategy, since the company\'s fixed overhead (rent, salaries, infrastructure) is shared across all products, not duplicated per package.',
    source: 'Calculated: Σ(Package Contribution Margins) − Fixed Costs',
  },

  netIncomePerUnit: {
    title: 'Net Income per Unit',
    description:
      'The profit earned on each individual sale after all variable costs. Formula: Price × (1 − Deduction Rate) − S1 Acquisition Cost − S2 Rep Cost − Feature COGS. If this number is negative, the package loses money on every sale regardless of volume — no amount of scale can fix it. This metric does not include fixed costs.',
    source: 'CFO FEB 2026 formula: Price × 73.4% − ($93.39 / close rate) − $189 − feature costs',
  },

  // ─── S1/S2 FUNNEL MODEL ───

  funnelModel: {
    title: 'S1/S2 Sales Funnel',
    description:
      'AlterMe uses a two-stage sales funnel. S1 (Stage 1) is a lower-cost, digitally-sold lead generation offer — like a $99 DNA kit — marketed to a mass audience. S2 (Stage 2) is the full product package sold via call center to S1 buyers. Each S2 package in the portfolio can have a different S1 offer with different costs and close rates. The funnel flow: S1 Purchase → Kit Return (85%) → Consultation (85%) → S2 Close (varies by package). The conjoint study measures cold-market demand, which is applied as a scenario proxy for the S1-to-S2 conversion rate.',
    source: 'AlterMe business model',
  },

  s1OfferName: {
    title: 'S1 Offer',
    description:
      'The Stage 1 lead generation offer that feeds into this S2 package. Each package can have a different S1 — for example, the full $1,499 package might use a $99 DNA Kit as its S1, while a $499 ring-only package might use a free Health Assessment. The S1 cost and S2 close rate determine the customer acquisition cost for this package.',
    source: 'AlterMe S1/S2 funnel model',
  },

  // ─── DEMAND MODES ───

  ceilingVsSplit: {
    title: 'Ceiling vs. Split Demand',
    description:
      'Two ways to estimate demand when offering multiple packages simultaneously. Ceiling (optimistic): each package captures its full conjoint demand independently, assuming no cannibalization — customers who would buy Package A don\'t trade down to Package C. Split (conservative): total addressable demand per market segment is shared proportionally across packages based on relative attractiveness. Reality falls between these two estimates. Use ceiling as an upper bound and split as a lower bound.',
    source: 'Portfolio demand allocation model',
  },

  // ─── COST OVERRIDES ───

  costOverrides: {
    title: 'Per-Package Cost Parameters',
    description:
      'Each package can have its own cost structure because different S1 offers and S2 products have fundamentally different economics. A $499 ring-only package has a cheaper S1 kit, higher close rate, and lower COGS than a $1,499 all-features package. When a parameter is not overridden, it falls back to the global default from the Shared Assumptions drawer. Amber-highlighted values indicate custom overrides.',
    source: 'Per-package cost model',
  },

  // ─── PAYMENT TERMS INSIGHT ───

  paymentTermsInsight: {
    title: 'Payment Terms Impact',
    description:
      'The conjoint study found that all three payment options (Pay in Full, Monthly 0% APR, Monthly with Interest) have nearly equal demand share — all within 1.7 percentage points of each other. This means payment structure is NOT a significant purchase driver. AlterMe could potentially eliminate 0% APR financing (which costs margin) with negligible demand impact.',
    source: 'CBC Study — payment attribute shares: Pay in Full 32.3%, Monthly 0% APR 33.8%, Monthly with Interest 34.0%',
  },

  // ─── SEGMENT DEMOGRAPHICS ───

  segmentDemographics: {
    title: 'Segment Demographic Profiles',
    description:
      'Respondents are assigned to the three k-means segments based on behavioral similarity (none rate and price distribution patterns). Each profile shows demographics, attitudes, purchase factors, brand consideration, wearable device ownership, and feature preferences for that segment. Note: segment assignment is an approximation since the raw conjoint utility scores are not available — it uses observable behavioral patterns that align with each segment\'s known profile.',
    source: 'Approximated from CBC Study respondent behavior, n=1,000',
  },

  demographicCrossTab: {
    title: 'Demographic Cross-Tab',
    description:
      'Filter respondents by demographic dimensions (gender, age, income, region, health goal, spending, wearable ownership) to see how demand curves, segment composition, and preferences shift for different audience slices. Use this to answer questions like "What do women aged 35-49 who own an Apple Watch want?" The demand curve shows the filtered group vs. the overall population.',
    source: 'CBC Study respondent-level data, n=1,000',
  },

  // ─── PORTFOLIO METRICS ───

  wasp: {
    title: 'Weighted Average Selling Price (WASP)',
    description:
      'The average price per unit sold across all packages in the portfolio, weighted by each package\'s unit volume. Calculated as Total Gross Revenue / Total S2 Units. A higher WASP means the portfolio skews toward premium packages. If WASP drops significantly as you add cheaper tiers, it signals revenue dilution from trade-down.',
    source: 'Calculated: Total Gross Revenue ÷ Total S2 Units',
  },

  s1ROI: {
    title: 'S1 Investment ROI',
    description:
      'The return on S1 acquisition investment across the entire portfolio. Calculated as (Total Net Revenue − Total S1 Acquisition Costs) / Total S1 Acquisition Costs. A value above 0% means the portfolio generates more net revenue than it spends on S1 customer acquisition. A negative value means the S1 investment is not being recovered from S2 sales alone.',
    source: 'Calculated: (Total Net Revenue − Total S1 Investment) ÷ Total S1 Investment',
  },

  monthsToBreakeven: {
    title: 'Months to Breakeven',
    description:
      'How many months of operation until cumulative contribution margins cover the fixed cost base. If the portfolio is already profitable (monthly P&L > 0), this shows "Now." If the portfolio has negative P&L but positive contribution margin, it shows the number of months until cumulative contribution catches up. If contribution margin is also negative, breakeven is not achievable.',
    source: 'Calculated from monthly portfolio P&L trajectory',
  },

  contributionMarginPct: {
    title: 'Contribution Margin %',
    description:
      'Total contribution margin as a percentage of total net revenue across the portfolio. Shows what fraction of every revenue dollar remains after variable costs (S1 acquisition, S2 rep, feature COGS) to cover fixed costs and generate profit. Higher is better — a 40% margin means 40 cents of every net revenue dollar covers overhead.',
    source: 'Calculated: Total Contribution ÷ Total Net Revenue',
  },

  timeSeriesProjection: {
    title: '12-Month P&L Projection',
    description:
      'Projects the portfolio\'s cumulative P&L over 12 months with an adjustable S1 lead growth rate. Month 1 uses the current S1 lead volume; subsequent months grow by the specified rate. This adds a time dimension to the otherwise static monthly snapshot, showing the path to profitability (or deepening losses) over a year.',
    interactiveNote:
      'Use the S1 Lead Growth slider to model scenarios: 0% = flat leads, 5% = moderate growth, 10% = aggressive scaling.',
    source: 'Calculated: monthly portfolio P&L with compounding S1 growth',
  },

  ltvCalculator: {
    title: 'Customer Lifetime Value (LTV)',
    description:
      'Models the total value of a customer over 24 months, factoring in monthly retention rate, recurring revenue (subscriptions, refills), and upsell potential (upgrading to a higher tier). Use this to evaluate whether loss-leader S1 tiers can be justified through long-term customer value. The payback period shows when cumulative LTV exceeds acquisition cost.',
    interactiveNote:
      'Adjust retention rate, monthly recurring revenue, and upsell rate/revenue to model different scenarios.',
    source: 'User-adjustable LTV model',
  },

  scenarioManager: {
    title: 'Scenario Manager',
    description:
      'Save your current portfolio configuration (all packages, assumptions, and settings) as a named scenario. Load saved scenarios to compare different portfolio strategies side by side. Scenarios are persisted to the database and survive page refreshes. Each user\'s scenarios are private.',
    interactiveNote:
      'Click "Save Current" to snapshot the current state. Click "Load" on any saved scenario to restore it. The current P&L is shown for quick comparison.',
  },

  financialViewMode: {
    title: 'Portfolio vs. Single-Product Analysis',
    description:
      'Toggle between two analysis modes. Portfolio Analysis shows combined economics across all packages with fixed costs applied once. Single-Product Analysis shows traditional per-price-point analysis where each configuration bears the full fixed cost burden. Use Portfolio mode for strategic portfolio decisions; use Single-Product mode for individual price point analysis.',
  },

  competitivePositioning: {
    title: 'Competitive Positioning Map',
    description:
      'A directional positioning map showing where your packages sit relative to known competitors in the wellness/health-tech space. The X-axis represents feature breadth (number of enabled features, from generic to comprehensive). The Y-axis represents personalization depth (proportion of personalization-oriented features like DNA Health Report, Personalized Wellness Plan, Nutrition Tracking). Competitor positions are approximate estimates for visual context — they are not derived from conjoint data.',
    interactiveNote:
      'Toggling features on/off in the Package Builder will shift your packages on this map. Competitors are fixed reference points.',
  },
};
